/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../Header";
import Sidebar from "../../Sidebar";
import { imguser } from "../../imagepath";
import { DatePicker } from "antd";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import config from "../../../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EditProfile = () => {
  const userId = localStorage.getItem("userUUID");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pinCodeError, setPinCodeError] = useState("");
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    dob: null,
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pin_code: "",
    profile_image: null,
  });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch user data
  useEffect(() => {
    if (userId) {
      fetch(`${config.API_URL}get_userdata.php?user_id=${userId}`)
        .then((response) => response.json())
        .then((data) => {
          if (data.success === "1") {
            const user = data.user;
            setFormData({
              first_name: user.first_name || "",
              last_name: user.last_name || "",
              email: user.email || "",
              phone: user.phone || "",
              dob: user.dob ? new Date(user.dob) : null,
              address_line1: user.address_line1 || "",
              address_line2: user.address_line2 || "",
              city: user.city || "",
              state: user.state || "",
              pin_code: user.pin_code || "",
              profile_image: user.profile_image
                ? `${config.API_URL}uploads/profile_images/user_images/${user.profile_image}`
                : null,
            });
          } else {
            toast.error(data.message || "Failed to fetch user data.");
          }
        })
        .catch((error) => {
          console.error("Error fetching user data:", error);
          toast.error("Error fetching user data.");
        })
        .finally(() => setLoading(false));
    } else {
      toast.error("User ID not found.");
      setLoading(false);
      navigate("/login");
    }
  }, [userId, navigate]);

  // Fetch city and state based on pin code
  const fetchCityAndState = async (pinCode) => {
    if (pinCode.length !== 6) {
      setPinCodeError("Pin code must be 6 digits");
      return;
    }

    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pinCode}`);
      const data = await response.json();
      if (data && data[0]?.Status === "Success" && data[0]?.PostOffice?.length) {
        const { District, State } = data[0].PostOffice[0];
        setFormData((prev) => ({
          ...prev,
          city: District,
          state: State,
          pin_code: pinCode,
        }));
        setPinCodeError("");
      } else {
        setFormData((prev) => ({
          ...prev,
          city: "",
          state: "",
          pin_code: pinCode,
        }));
        setPinCodeError("Invalid Pin Code");
      }
    } catch (error) {
      console.error("Error fetching city and state:", error);
      setPinCodeError("Error fetching data");
      setFormData((prev) => ({
        ...prev,
        city: "",
        state: "",
        pin_code: pinCode,
      }));
    }
  };

  // Handle input changes
  const onInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "pin_code") {
      const pinCode = value.replace(/\D/g, "");
      if (pinCode.length > 6) {
        setPinCodeError("Pin code should not exceed 6 digits");
        return;
      } else if (pinCode.length < 6 && pinCode.length > 0) {
        setPinCodeError("Pin code must be 6 digits");
      } else {
        setPinCodeError("");
      }
      setFormData((prev) => ({
        ...prev,
        pin_code: pinCode,
        city: "",
        state: "",
      }));
      if (pinCode.length === 6) {
        fetchCityAndState(pinCode);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle date change
  const onDateChange = (date) => {
    setFormData({ ...formData, dob: date });
  };

  // Handle file upload (Open Document)
  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setFormData({ ...formData, profile_image: file });
    } else {
      toast.error("Please select an image file.");
    }
  };

  // Open camera
  const handleOpenCamera = async () => {
    setIsCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      toast.error("Failed to access camera. Ensure camera permissions are granted.");
      setIsCameraOpen(false);
    }
  };

  // Capture photo from camera
  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `profile_${Date.now()}.png`, { type: "image/png" });
      setFormData({ ...formData, profile_image: file });
      stopCamera();
    }, "image/png");
  };

  // Stop camera
  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsCameraOpen(false);
  };

  // Open document picker
  const handleOpenDocument = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Submit form
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error("Please fill in all required fields (First Name, Last Name, Email).");
      setIsSubmitting(false);
      return;
    }

    if (formData.pin_code && formData.pin_code.length !== 6) {
      toast.error("Pin code must be 6 digits.");
      setIsSubmitting(false);
      return;
    }

    const submitData = new FormData();
    submitData.append("user_id", userId);
    submitData.append("first_name", formData.first_name);
    submitData.append("last_name", formData.last_name);
    submitData.append("email", formData.email);
    submitData.append("phone", formData.phone);
    submitData.append("dob", formData.dob ? formData.dob.toISOString().split("T")[0] : "");
    submitData.append("address_line1", formData.address_line1);
    submitData.append("address_line2", formData.address_line2);
    submitData.append("city", formData.city);
    submitData.append("state", formData.state);
    submitData.append("pin_code", formData.pin_code);
    if (formData.profile_image && formData.profile_image instanceof File) {
      submitData.append("profile_image", formData.profile_image);
    }

    fetch(`${config.API_URL}update_user_profile.php`, {
      method: "POST",
      body: submitData,
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("Update profile response:", data);
        if (
          data.success === "1" ||
          data.success === true ||
          data.success === "true" ||
          data.status === "success"
        ) {
          toast.success(data.message || "Profile updated successfully!", {
            onClose: () => navigate("/patient-dashboard"),
          });
        } else {
          toast.error(data.message || "Failed to update profile.");
        }
      })
      .catch((error) => {
        console.error("Error updating profile:", error);
        toast.error("Error updating profile.");
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} />
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Dashboard </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Edit Profile</li>
                </ul>
              </div>
            </div>
          </div>
          {loading ? (
            <div>Loading...</div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="card-box">
                <h3 className="card-title">Basic Information</h3>
                <div className="row">
                  <div className="col-md-12">
                    <div className="profile-img-wrap">
                      <img
                        className="inline-block"
                        src={
                          formData.profile_image instanceof File
                            ? URL.createObjectURL(formData.profile_image)
                            : formData.profile_image || imguser
                        }
                        alt="user"
                      />
                      <div className="fileupload btn d-flex align-items-center">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm me-2"
                          onClick={handleOpenCamera}
                          title="Open Camera"
                          style={{
                            padding: "8px 12px",
                            transition: "all 0.3s ease",
                            borderRadius: "50px",
                          }}
                        >
                          <FeatherIcon icon="camera" size="18" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={handleOpenDocument}
                          title="Open Document"
                          style={{
                            padding: "8px 12px",
                            transition: "all 0.3s ease",
                            borderRadius: "50px",
                          }}
                        >
                          <FeatherIcon icon="file" size="18" />
                        </button>
                        <input
                          className="upload"
                          type="file"
                          accept="image/*"
                          ref={fileInputRef}
                          onChange={onFileChange}
                          style={{ display: "none" }}
                        />
                      </div>
                      <style>{`
                        .fileupload .btn:hover {
                          background-color: #ee7c00;
                          color: white;
                          transform: scale(1.1);
                        }
                        .fileupload .btn:active {
                          transform: scale(0.95);
                        }
                      `}</style>
                    </div>
                    {isCameraOpen && (
                      <div className="camera-section mt-3">
                        <video
                          ref={videoRef}
                          autoPlay
                          style={{ width: "100%", maxHeight: "200px", borderRadius: "5px" }}
                        />
                        <canvas ref={canvasRef} style={{ display: "none" }} />
                        <div className="mt-2 d-flex justify-content-center">
                          <button
                            type="button"
                            className="btn btn-primary mb-5 mx-2"
                            onClick={handleCapturePhoto}
                          >
                            Capture
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary mb-5"
                            onClick={stopCamera}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="profile-basic">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group local-forms">
                            <label className="focus-label">First Name</label>
                            <input
                              type="text"
                              className="form-control floating"
                              name="first_name"
                              value={formData.first_name}
                              onChange={onInputChange}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group local-forms">
                            <label className="focus-label">Last Name</label>
                            <input
                              type="text"
                              className="form-control floating"
                              name="last_name"
                              value={formData.last_name}
                              onChange={onInputChange}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group local-forms">
                            <label className="focus-label">Email</label>
                            <input
                              type="email"
                              className="form-control floating"
                              name="email"
                              value={formData.email}
                              onChange={onInputChange}
                              disabled
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group local-forms">
                            <label className="focus-label">Phone Number</label>
                            <input
                              type="text"
                              className="form-control floating"
                              name="phone"
                              value={formData.phone}
                              onChange={onInputChange}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group local-forms">
                            <label className="focus-label">Birth Date</label>
                            <div className="cal-icon">
                              <DatePicker
                                className="form-control datetimepicker"
                                selected={formData.dob}
                                onChange={onDateChange}
                                dateFormat="yyyy-MM-dd"
                                suffixIcon={null}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-box">
                <h3 className="card-title">Contact Information</h3>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group local-forms">
                      <label className="focus-label">Address Line 1</label>
                      <input
                        type="text"
                        className="form-control floating"
                        name="address_line1"
                        value={formData.address_line1}
                        onChange={onInputChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="form-group local-forms">
                      <label className="focus-label">Address Line 2</label>
                      <input
                        type="text"
                        className="form-control floating"
                        name="address_line2"
                        value={formData.address_line2}
                        onChange={onInputChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group local-forms">
                      <label className="focus-label">Pin Code</label>
                      <input
                        type="text"
                        className={`form-control floating ${pinCodeError ? "is-invalid" : ""}`}
                        name="pin_code"
                        value={formData.pin_code}
                        onChange={onInputChange}
                      />
                      {pinCodeError && (
                        <span className="text-danger">{pinCodeError}</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group local-forms">
                      <label className="focus-label">City</label>
                      <input
                        type="text"
                        className="form-control floating"
                        name="city"
                        value={formData.city}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group local-forms">
                      <label className="focus-label">State</label>
                      <input
                        type="text"
                        className="form-control floating"
                        name="state"
                        value={formData.state}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-center m-t-20">
                <button
                  className="btn btn-primary submit-btn"
                  type="submit"
                  disabled={isSubmitting || pinCodeError}
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default EditProfile;